import merge from 'lodash/merge';
import { equals, isEmpty, remove, sort } from 'ramda';
import type { Location, NodeEntry, NodeMatch } from 'slate';
import { Editor, Node, Path, Point, Range } from 'slate';

import { getNodeSuggestedEdits } from './suggested-edits';
import type {
  AlineDescendant,
  AlineNode,
  BaseEdit,
  EditBounds,
  EditEntries,
  EditEntry,
  EditEntryGroup,
  EditEntryOrGroup,
  SuggestedEdit,
} from '../internal';
import {
  findSuggestedEditIndex,
  isEditEntryGroup,
  isInsertInstructionFieldEdit,
  isParagraphElement,
  isParagraphGlyphEdit,
  isTableCellElement,
  isTableElement,
  Queries,
} from '../internal';

export function compareFnSortEntries(entryA: EditEntry, entryB: EditEntry): number {
  const { range: rangeA } = entryA;
  const { range: rangeB } = entryB;

  const [startA, endA] = Range.edges(rangeA);
  const [startB, endB] = Range.edges(rangeB);

  /**
   * If startA is before startB, sort entryA first and vice versa.
   * If startA and startB are equal points, counterintuitively, we want to sort the "larger range"
   * first.  So, if endA is after endB, entryA comes first and vice versa.
   */
  if (Point.isBefore(startA, startB)) return -1;
  if (Point.isAfter(startA, startB)) return 1;
  if (Point.isAfter(endA, endB)) return -1;
  if (Point.isAfter(endB, endA)) return 1;

  return 0;
}

/**
 * Given a node and a new suggested edit, returns the new suggested edits that
 * should be present on the node. Note: Returns null if there are *no changes*
 * to the existing suggested edits. Null does NOT mean there should be no
 * suggested edits on the node, just that there are no changes to suggested
 * edits.
 */
function newEdits<T extends AlineDescendant>(
  node: T,
  suggestedEdit: NonNullable<T['suggestedEdits']>[number]
): T['suggestedEdits'] | null {
  const safeSuggestedEdit = {
    ...suggestedEdit,
    createdAt: suggestedEdit.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } satisfies Required<Omit<BaseEdit, 'groupId' | 'reasoning'>> &
    Pick<BaseEdit, 'groupId' | 'reasoning'>;

  if (!node.suggestedEdits) return [safeSuggestedEdit];

  const existingSuggestionIndex =
    node.suggestedEdits.findIndex(
      edit => edit.type === safeSuggestedEdit.type && edit.authorId === safeSuggestedEdit.authorId
    ) ?? -1;
  if (existingSuggestionIndex !== -1) {
    const existingEdit = node.suggestedEdits[existingSuggestionIndex];

    // If the user is making exactly the same edit as already exists, there are no new edits.
    if (equals(suggestedEdit.value, existingEdit.value)) return null;

    // Preserve the original createdAt timestamp but update the updatedAt timestamp
    const mergedEdit = merge({}, existingEdit, {
      updatedAt: new Date().toISOString(),
      value: suggestedEdit.value,
    });
    const existingEditRemoved = remove(existingSuggestionIndex, 1, node.suggestedEdits);

    // Since the user is modifying their existing edit, bump it to the front of the stack so it gets rendered.
    return [mergedEdit, ...existingEditRemoved];
  }

  /**
   * We know there isn't already an existing edit by this user.  Add the new edit to suggestedEdits.
   */
  return [safeSuggestedEdit, ...node.suggestedEdits];
}

const trackedChangesQueries = {
  /**
   * Return the active edit(s) based on the selection, if any.  Multiple edits can be active if they have
   * the same exact range.
   */
  activeEdits: (editor: Editor, editEntries: EditEntries): EditEntry[] | null => {
    const { selection } = editor;
    if (!selection) return null;

    let potentialActiveEdits = editEntries.filter(edit => Range.surrounds(edit.range, selection));

    /**
     * Fallback when the selection isn't nested inside any single edit — e.g. a
     * card click selected a super-group's union range (wider than any member).
     * Treat the edits the selection contains as active so the card stays active.
     */
    if (!potentialActiveEdits.length)
      potentialActiveEdits = editEntries.filter(edit => Range.surrounds(selection, edit.range));

    if (!potentialActiveEdits.length) return null;
    if (potentialActiveEdits.length === 1) return potentialActiveEdits;

    const sortedBySmallestRange = sort((a: EditEntry, b: EditEntry) => {
      const aString = Editor.string(editor, a.range);
      const bString = Editor.string(editor, b.range);

      if (aString.length < bString.length) return -1;
      if (bString.length < aString.length) return 1;

      return 0;
    }, potentialActiveEdits);
    const [smallestRangeEntry] = sortedBySmallestRange;

    return sortedBySmallestRange.filter(({ range }) =>
      Range.equals(range, smallestRangeEntry.range)
    );
  },

  /**
   * Returns edit entries that specify the range of all unique edits.
   */
  editRanges: (editor: Editor, entriesWithEdits: NodeEntry<AlineDescendant>[]): EditEntries => {
    const editEntries: EditEntries = [];
    const editsBounds: EditBounds[] = [];

    for (const [index, [node, path]] of entriesWithEdits.entries()) {
      const isLastIndex = index === entriesWithEdits.length - 1;

      // If we're at index 0, we need to create the initial set of edit bounds.
      if (index === 0) {
        for (const suggestedEdit of getNodeSuggestedEdits(node)) {
          const editBounds: EditBounds = {
            edit: suggestedEdit,
            endPath: path,
            startPath: path,
          };

          editsBounds.push(editBounds);
        }

        if (isLastIndex) pushEditState(editor, editsBounds, editEntries);

        continue;
      }

      /**
       * The node may have edits that are not yet in editsBounds or that are in editsBounds and
       * are contiguous edits.
       */
      for (const suggestedEdit of getNodeSuggestedEdits(node)) {
        /**
         * Paragraph-mark edits stay as their own EditEntry (one per pilcrow):
         * deep-equal-merging them here would falsely fuse separate gestures
         * across the text between their pilcrows. The downstream merger
         * (`mergeContiguousInsertDeleteEditsByAuthor`) collapses adjacent
         * same-author entries when appropriate.
         */
        const existingEditBoundsIndex = isParagraphGlyphEdit(suggestedEdit)
          ? -1
          : findSuggestedEditIndex(
              editsBounds.map(b => b.edit),
              suggestedEdit
            );

        // If we're not already building a suggested edit bounds, start.
        if (existingEditBoundsIndex === -1) {
          editsBounds.push({ edit: suggestedEdit, endPath: path, startPath: path });
          continue;
        }

        const editBounds = editsBounds[existingEditBoundsIndex];
        const shouldMerge = Queries.shouldMergeEqualEdits(editor, editBounds.endPath, path);

        // Update the end path of the existing edit since its a continuous edit with a single range that should be merged.
        if (shouldMerge) {
          editsBounds[existingEditBoundsIndex].endPath = path;
          continue;
        }

        /**
         * We're not merging with the edit in editsBounds, so we push our current state
         * and then add a new edit bounds of the same type to editsBounds.
         */
        const startEdit = rangeAnchorForEdit(editor, editBounds.edit, editBounds.startPath);
        const endEdit = Editor.end(editor, editBounds.endPath);
        editEntries.push({ edit: editBounds.edit, range: { anchor: startEdit, focus: endEdit } });
        editsBounds[existingEditBoundsIndex] = {
          edit: suggestedEdit,
          endPath: path,
          startPath: path,
        };
      }

      if (isLastIndex) pushEditState(editor, editsBounds, editEntries);
    }

    return editEntries;
  },

  getSuggestedEdit: (
    editor: Editor,
    options: { at: Path; type: SuggestedEdit['type']; authorId?: string }
  ): SuggestedEdit | undefined => {
    const { at, authorId, type } = options;

    const node = Node.get(editor, at);
    if (Editor.isEditor(node)) return;

    const edits = node.suggestedEdits ?? [];
    return edits.find((edit: SuggestedEdit) => {
      const sameEditType = edit.type === type;
      const sameAuthor = !authorId || edit.authorId === authorId;
      return sameEditType && sameAuthor;
    });
  },

  /**
   * Determines if a row or cell edit represents an entire table or column insertion or deletion
   */
  isEntireTableOrColumnSuggestion: (editor: Editor, editEntry: EditEntry): boolean => {
    const { range } = editEntry;
    const { rowEntries, tableEntry } = Queries.getContiguousTableRows(editor, { at: range });

    if (!tableEntry || rowEntries.length === 0) return false;

    return rowEntries.length === tableEntry[0].children.length;
  },

  /**
   * See above for function implementation, overload definitions, and notes.
   */
  newEdits,

  /**
   * Gets the next suggested edit after the user's selection.
   */
  nextChange: (editor: Editor, editEntries: EditEntries): EditEntry | null => {
    if (!editor.selection) return null;

    const [selStart] = Range.edges(editor.selection);
    const sortedEditEntriesAfterStart = editEntries
      .filter(editEntry => {
        const [entryStart] = Range.edges(editEntry.range);
        return Point.isAfter(entryStart, selStart);
      })
      .sort(compareFnSortEntries);
    if (!sortedEditEntriesAfterStart.length) {
      const sortedEditEntries = [...editEntries].sort(compareFnSortEntries);

      // If there's no edit entry after the start of the selection, return the first edit entry in the doc, if any.
      return sortedEditEntries[0] ?? null;
    }

    return sortedEditEntriesAfterStart[0];
  },

  /**
   * Gets the previous suggested edit before the user's selection.
   */
  prevChange: (editor: Editor, editEntries: EditEntries): EditEntry | null => {
    if (!editor.selection) return null;

    const [selStart] = Range.edges(editor.selection);
    const sortedEditEntriesBeforeStart = editEntries
      .filter(editEntry => {
        const [entryStart] = Range.edges(editEntry.range);
        return Point.isBefore(entryStart, selStart);
      })
      .sort(compareFnSortEntries);
    if (!sortedEditEntriesBeforeStart.length) {
      const sortedEditEntries = [...editEntries].sort(compareFnSortEntries);
      // If there are no edit entries before the start of the selection, simply return the edit entry at the end of the doc.
      return sortedEditEntries[sortedEditEntries.length - 1] ?? null;
    }

    // Return the last edit entry in the array that is before the start of the selection.
    return sortedEditEntriesBeforeStart[sortedEditEntriesBeforeStart.length - 1];
  },

  /**
   * Return the edits that are selected from the edits passed in.
   * A grid selection is matched against the cells it highlights. A table's format edits come
   * back whole, because the review box merges them into one card and resolves it in one go.
   */
  selectedEdits: (editor: Editor, editEntries: EditEntries): EditEntry[] => {
    const { selection } = editor;
    if (!selection) return [];

    const gridCellRanges = Queries.getGridSelectionCellEntries(editor, {
      includeVerticalMergeContinuations: true,
    })?.map(([, cellPath]) => Editor.range(editor, cellPath));
    const selectedRanges = gridCellRanges ?? [selection];

    const getIsSelected = (editEntry: EditEntry) =>
      selectedRanges.some(range => Boolean(Range.intersection(editEntry.range, range)));

    const tableFormatEntries = getTouchedTableFormatEntries(editor, editEntries, getIsSelected);

    return editEntries.filter(
      editEntry => tableFormatEntries.has(editEntry) || getIsSelected(editEntry)
    );
  },

  /**
   * Determines whether 2 suggested edits that are deeply equal should be merged into a single
   * edit entry.
   */
  shouldMergeEqualEdits: (editor: Editor, existingEditPath: Path, nextEditPath: Path): boolean => {
    const isNextPathContiguous = Queries.pathIsAbutting(editor, existingEditPath, nextEditPath);
    // Perf.  Bail early if the paths aren't contiguous since edits at non-contiguous paths should never be merged.
    if (!isNextPathContiguous) return false;

    const tableCellEntryAboveExisting = Editor.above(editor, {
      at: existingEditPath,
      match: isTableCellElement,
    });
    const tableCellEntryAboveNext = Editor.above(editor, {
      at: nextEditPath,
      match: isTableCellElement,
    });
    if (tableCellEntryAboveExisting) {
      if (!tableCellEntryAboveNext) return false;

      const [, tableCellPathAboveExisting] = tableCellEntryAboveExisting;
      const [, tableCellPathAboveNext] = tableCellEntryAboveNext;

      // If edits are at contiguous paths w/in the same table cell, they can be merged.
      return Path.equals(tableCellPathAboveExisting, tableCellPathAboveNext);
    }

    // Edits can be merged if there is no table cell above next edit.  Otherwise, don't merge the edits b/c the next edit is in a table cell and prior edit is not.
    return !tableCellEntryAboveNext;
  },
  /**
   * Get the range of each suggested edit(s) sorted by the order of appearance in the document.
   * Useful for displaying information about proposed edits and accepting/rejecting edits (i.e., tracked changes 'balloons').
   */
  sortedEditRanges: (
    editor: Editor,
    options: { at?: Location; match?: NodeMatch<AlineNode>; voids?: boolean } = {}
  ): EditEntries => {
    const { at = [], match = () => true, voids = true } = options;
    const nodeEntriesWithEdits = [
      ...Editor.nodes<AlineDescendant>(editor, {
        at,
        match: (node, path) =>
          !Editor.isEditor(node) &&
          ((Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits) &&
            match(node, path)) ||
            (isParagraphElement(node) &&
              Array.isArray(node.glyphProps?.suggestedEdits) &&
              !isEmpty(node.glyphProps.suggestedEdits))),
        voids,
      }),
    ];
    const entries = Queries.editRanges(editor, nodeEntriesWithEdits);

    return entries.sort(compareFnSortEntries);
  },
};

function pushEditState(editor: Editor, editsBounds: EditBounds[], editEntries: EditEntries): void {
  for (const editBounds of editsBounds) {
    const startEdit = rangeAnchorForEdit(editor, editBounds.edit, editBounds.startPath);
    const endEdit = Editor.end(editor, editBounds.endPath);
    const range: Range = { anchor: startEdit, focus: endEdit };

    editEntries.push({
      edit: editBounds.edit,
      range,
    });
  }
}

/**
 * Anchor point for an EditEntry's range. Paragraph-mark edits live at the
 * paragraph's trailing pilcrow (ECMA-376 §17.5.2.7), so they anchor at the
 * path's end — keeping pre-existing content out of the card body and selection
 * highlight. All other edit types anchor at the path's start.
 */
function rangeAnchorForEdit(editor: Editor, edit: SuggestedEdit, path: Path): Point {
  return isParagraphGlyphEdit(edit) ? Editor.end(editor, path) : Editor.start(editor, path);
}

function findParentTable(editor: Editor, entries: EditEntry[]) {
  const firstEntry = entries[0];
  if (!firstEntry) return undefined;
  return Editor.above(editor, { at: firstEntry.range, match: isTableElement });
}

/**
 * Collapses edit entries into `EditEntryGroup` items using a pipeline of five steps:
 *
 * 1. `groupByKey`                              — group entries that share the same `groupId`
 * 2. `mergeContiguousInsertDeleteEditsByAuthor`— fuse adjacent same-author insert/delete runs
 * 3. `mergeTablePropertyEditsByAuthor`         — merge same-table format edits by the same author
 * 4. `assignGroupLabels`                       — compute a human-readable label for each group
 * 5. `unwrapSingleMemberGroups`                — unwrap 1-item groups back to plain `EditEntry` items
 *
 * Document order is preserved throughout (group position = position of first member).
 */
export function groupEditEntries(editor: Editor, editEntries: EditEntries): EditEntryOrGroup[] {
  const resolvableEntries = editEntries.filter(entry => Queries.hasRange(editor, entry.range));
  const grouped = groupByKey(resolvableEntries);
  const polarityMerged = mergeContiguousInsertDeleteEditsByAuthor(editor, grouped);
  const tableMerged = mergeTablePropertyEditsByAuthor(editor, polarityMerged);
  const labeled = assignGroupLabels(editor, tableMerged);
  return unwrapSingleMemberGroups(labeled);
}

function getRowGroupLabel(
  editor: Editor,
  entries: EditEntry[],
  verb: string,
  rowEditType: 'insertTableRow' | 'removeTableRow'
) {
  const rowEntries = entries.filter(e => e.edit.type === rowEditType);
  if (!rowEntries.length) return `${verb} row`;

  const tableEntry = findParentTable(editor, rowEntries);
  if (!tableEntry) return `${verb} row`;

  const [table] = tableEntry;
  const totalRows = table.children.length;
  const { groupId } = rowEntries[0].edit;
  const editType = rowEntries[0].edit.type;

  const affectedRows = table.children.filter(row =>
    row.suggestedEdits?.some(e => e.type === editType && e.groupId === groupId)
  ).length;

  if (affectedRows === totalRows) return `${verb} table`;
  if (affectedRows <= 1) return `${verb} row`;
  return `${verb} ${affectedRows} rows`;
}

function getCellGroupLabel(
  editor: Editor,
  entries: EditEntry[],
  verb: string,
  cellEditType: 'insertTableCell' | 'removeTableCell'
) {
  const cellEntries = entries.filter(e => e.edit.type === cellEditType);
  if (!cellEntries.length) return `${verb} column`;

  const tableEntry = findParentTable(editor, cellEntries);
  if (!tableEntry) return `${verb} column`;

  const [table] = tableEntry;
  const firstRow = table.children[0];
  if (!firstRow) return `${verb} column`;

  const { groupId } = cellEntries[0].edit;
  const editType = cellEntries[0].edit.type;

  const affectedColumns = firstRow.children.filter(cell =>
    cell.suggestedEdits?.some(e => e.type === editType && e.groupId === groupId)
  ).length;

  if (affectedColumns <= 1) return `${verb} column`;
  return `${verb} ${affectedColumns} columns`;
}

function getGroupLabel(editor: Editor, entries: EditEntry[]): string {
  const types = new Set(entries.map(e => e.edit.type));
  if (types.has('insertTableCell'))
    return getCellGroupLabel(editor, entries, 'Insert', 'insertTableCell');
  if (types.has('removeTableCell'))
    return getCellGroupLabel(editor, entries, 'Delete', 'removeTableCell');
  if (types.has('insertTableRow'))
    return getRowGroupLabel(editor, entries, 'Insert', 'insertTableRow');
  if (types.has('removeTableRow'))
    return getRowGroupLabel(editor, entries, 'Delete', 'removeTableRow');
  const tocInsert = entries.find(
    entry => isInsertInstructionFieldEdit(entry.edit) && entry.edit.instruction === 'TOC'
  );
  if (tocInsert) return 'Insert table of contents';

  const polarity = polarityOfEntries(entries);
  if (polarity === 'insert') {
    if (types.size === 1 && types.has('insertParagraph')) {
      return getParagraphGlyphGroupLabel(entries, 'Insert paragraph');
    }
    return `Insert: (${entries.length} items)`;
  }
  if (polarity === 'delete') {
    if (types.size === 1 && types.has('deleteParagraph')) {
      return getParagraphGlyphGroupLabel(entries, 'Delete paragraph');
    }
    return `Delete: (${entries.length} items)`;
  }
  return 'Formatted table';
}

// Exported so `@aline/client` shares one definition — separate sets would drift.
export const INSERT_POLARITY_TYPES = new Set<SuggestedEdit['type']>([
  'insertNode',
  'insertParagraph',
]);
export const DELETE_POLARITY_TYPES = new Set<SuggestedEdit['type']>([
  'deleteNode',
  'deleteParagraph',
]);

/** Returns the smallest range that contains every entry's range. */
export function unionRangeOfEntries(entries: EditEntry[]): Range {
  let anchor = Range.start(entries[0].range);
  let focus = Range.end(entries[0].range);
  for (const entry of entries) {
    const start = Range.start(entry.range);
    const end = Range.end(entry.range);
    if (Point.compare(start, anchor) < 0) anchor = start;
    if (Point.compare(end, focus) > 0) focus = end;
  }
  return { anchor, focus };
}

// Single shared polarity if all entries agree; null if they mix or include other types.
export function polarityOfEntries(entries: EditEntry[]): 'insert' | 'delete' | null {
  let polarity: 'insert' | 'delete' | null = null;
  for (const entry of entries) {
    const next = INSERT_POLARITY_TYPES.has(entry.edit.type)
      ? 'insert'
      : DELETE_POLARITY_TYPES.has(entry.edit.type)
        ? 'delete'
        : null;
    if (!next) return null;
    if (polarity && polarity !== next) return null;
    polarity = next;
  }
  return polarity;
}

function getParagraphGlyphGroupLabel(entries: EditEntry[], verb: string): string {
  const count = entries.length;
  return count > 1 ? `${verb}: (${count} times)` : `${verb}: (1 time)`;
}

const TABLE_PROPERTY_EDIT_TYPES = new Set<string>(['tableFormat', 'tableCellFormat']);

/**
 * Every entry of the table format cards the selection touches. The review box merges a table's fill
 * and border edits into a single card per author and accepts or rejects it whole, so a selection
 * that reaches any of them resolves that same card.
 */
function getTouchedTableFormatEntries(
  editor: Editor,
  editEntries: EditEntries,
  getIsSelected: (editEntry: EditEntry) => boolean
): Set<EditEntry> {
  const entries = new Set<EditEntry>();

  for (const item of groupEditEntries(editor, editEntries)) {
    if (!isEditEntryGroup(item)) continue;
    if (!item.editEntries.every(({ edit }) => TABLE_PROPERTY_EDIT_TYPES.has(edit.type))) continue;
    if (!item.editEntries.some(getIsSelected)) continue;

    for (const editEntry of item.editEntries) entries.add(editEntry);
  }

  return entries;
}

/**
 * Groups entries that share the same `groupId` into `EditEntryGroup` objects.
 * Entries without a `groupId` pass through as standalone `EditEntry` items.
 * Document order is preserved — a group is positioned where its first member appears.
 */
function groupByKey(editEntries: EditEntries): EditEntryOrGroup[] {
  const keyToGroup = new Map<string, EditEntryGroup>();
  const result: EditEntryOrGroup[] = [];

  for (const entry of editEntries) {
    const key = entry.edit.groupId;

    if (!key) {
      result.push(entry);
      continue;
    }

    const existingGroup = keyToGroup.get(key);
    if (existingGroup) {
      existingGroup.editEntries.push(entry);
    } else {
      const group: EditEntryGroup = { editEntries: [entry], groupId: key, groupLabel: '' };
      keyToGroup.set(key, group);
      result.push(group);
    }
  }

  return result;
}

/**
 * Greedily merges adjacent same-author, same-polarity items (adjacency per
 * `areAdjacent`). This gives the Word/Docs behavior where one gesture mixing
 * text edits and paragraph splits/merges renders as a single tracked change
 * card.
 */
function mergeContiguousInsertDeleteEditsByAuthor(
  editor: Editor,
  items: EditEntryOrGroup[]
): EditEntryOrGroup[] {
  if (!editor || items.length === 0) return items;

  const result: EditEntryOrGroup[] = [];
  let current: EditEntryGroup | null = null;
  let currentPolarity: 'insert' | 'delete' | null = null;
  let currentAuthorId: string | null = null;

  function flush() {
    if (current) result.push(current);
    current = null;
    currentPolarity = null;
    currentAuthorId = null;
  }

  for (const item of items) {
    const entries = 'editEntries' in item ? item.editEntries : [item];
    if (entries.length === 0) {
      flush();
      result.push(item);
      continue;
    }

    const itemPolarity = polarityOfEntries(entries);
    const itemAuthorId = entries[0].edit.authorId;
    const sameAuthorThroughout = entries.every(e => e.edit.authorId === itemAuthorId);

    if (!itemPolarity || !sameAuthorThroughout) {
      flush();
      result.push(item);
      continue;
    }

    const itemFirstStart = Range.start(entries[0].range);

    if (
      current &&
      currentPolarity === itemPolarity &&
      currentAuthorId === itemAuthorId &&
      areAdjacent(editor, current, itemFirstStart)
    ) {
      current.editEntries.push(...entries);
      continue;
    }

    flush();
    /**
     * Stable super-group identity (DOCX export, React keys). Prefer the first
     * member's `groupId` so the wrapper id agrees with what TrackedChangesBox and
     * DOCX export key on; fall back to author+polarity+start-path otherwise. Avoid
     * `createdAt` in the fallback — `newEdits` bumps it, churning the identity.
     */
    const firstStartPath = itemFirstStart.path.join(',');
    current = {
      editEntries: [...entries],
      groupId: entries[0].edit.groupId ?? `${itemAuthorId}::${itemPolarity}::${firstStartPath}`,
      groupLabel: '',
    };
    currentPolarity = itemPolarity;
    currentAuthorId = itemAuthorId;
  }
  flush();

  return result;
}

function areAdjacent(editor: Editor, group: EditEntryGroup, nextStart: Point): boolean {
  /**
   * Use the furthest-forward end across all members, not just the last-added
   * one — entries can arrive out of document order when the sort puts a wider
   * range first (e.g. a multi-paragraph glyph entry before a text entry that
   * starts at the same point).
   */
  let maxEnd: Point | null = null;
  for (const entry of group.editEntries) {
    const end = Range.end(entry.range);
    if (!maxEnd || Point.compare(end, maxEnd) > 0) maxEnd = end;
  }
  if (!maxEnd) return false;

  // Overlap or touch at the same point → mergeable.
  if (Point.compare(nextStart, maxEnd) <= 0) return true;
  if (!Queries.pathIsAbutting(editor, maxEnd.path, nextStart.path)) return false;
  /**
   * Path-abutting alone isn't enough: sibling paragraph-mark edits abut by path
   * but the paragraph between their pilcrows may hold pre-existing text from
   * separate gestures (e.g. "Red fox¶ jumps across¶"). Any untracked text in
   * the gap means the boundaries belong to different gestures and must not merge.
   *
   * Relies on `nextStart` following `maxEnd` in document order (guaranteed by
   * the `Point.compare <= 0` early-return and the document-sorted caller input).
   */
  return Editor.string(editor, { anchor: maxEnd, focus: nextStart }) === '';
}

/**
 * Merges `tableFormat` / `tableCellFormat` items that belong to the same table
 * into a single super-group, identified by sharing the same `authorId` and parent
 * table path. All other items pass through unchanged.
 *
 * Uses `Editor.above` to find the parent table for each item, so that collapsed
 * point-range edits (e.g. `tableCellFormat`) are grouped correctly regardless of
 * whether their ranges overlap.
 */
function mergeTablePropertyEditsByAuthor(
  editor: Editor,
  items: EditEntryOrGroup[]
): EditEntryOrGroup[] {
  if (!editor) return items;

  const groupByKey = new Map<string, EditEntryGroup>();
  const result: EditEntryOrGroup[] = [];

  for (const item of items) {
    const representativeEntry = 'editEntries' in item ? item.editEntries[0] : item;
    if (!representativeEntry || !TABLE_PROPERTY_EDIT_TYPES.has(representativeEntry.edit.type)) {
      result.push(item);
      continue;
    }

    const { authorId } = representativeEntry.edit;
    const entriesToMerge = 'editEntries' in item ? item.editEntries : [item];
    const tableEntry = Editor.above(editor, {
      at: representativeEntry.range,
      match: isTableElement,
    });

    if (!tableEntry) {
      result.push(item);
      continue;
    }

    const [, tablePath] = tableEntry;
    const key = `${authorId}::${tablePath.join(',')}`;

    const existing = groupByKey.get(key);
    if (existing) {
      for (const entry of entriesToMerge) existing.editEntries.push(entry);
    } else {
      const superGroup: EditEntryGroup = {
        editEntries: [...entriesToMerge],
        groupId: `${authorId}${JSON.stringify(tablePath)}`,
        groupLabel: '',
      };
      groupByKey.set(key, superGroup);
      result.push(superGroup);
    }
  }

  return result;
}

/** Assigns a human-readable `groupLabel` to every `EditEntryGroup` in the list. */
function assignGroupLabels(editor: Editor, items: EditEntryOrGroup[]): EditEntryOrGroup[] {
  for (const item of items) {
    if ('editEntries' in item) item.groupLabel = getGroupLabel(editor, item.editEntries);
  }
  return items;
}

/** Unwraps any `EditEntryGroup` that ended up with only one member back to a plain `EditEntry`. */
function unwrapSingleMemberGroups(items: EditEntryOrGroup[]): EditEntryOrGroup[] {
  return items.map(item =>
    'editEntries' in item && item.editEntries.length === 1 ? item.editEntries[0] : item
  );
}

export default trackedChangesQueries;
